import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from "react";
import authService from "../services/auth";

export interface User {
  id: string;
  email: string;
  name: string;
  createdAt: string;
  updatedAt: string;
}

export interface Organization {
  id: string;
  name: string;
  createdAt: string;
  updatedAt: string;
}

export interface AuthData {
  access_token: string;
  user: User;
  org: Organization;
}

interface AuthContextType {
  user: User | null;
  org: Organization | null;
  login: (credentials: {
    email: string;
    password: string;
  }) => Promise<AuthData>;
  signup: (userData: {
    email: string;
    password: string;
    name: string;
  }) => Promise<AuthData>;
  loginWithGoogle: (googleToken: string, orgData?: any) => Promise<AuthData>;
  getMe: () => Promise<AuthData>;
  logout: () => void;
  isAuthenticated: boolean;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [org, setOrg] = useState<Organization | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if user is already authenticated
    const checkAuth = async () => {
      try {
        if (authService.isAuthenticated()) {
          const user = authService.getCurrentUser();
          const org = authService.getCurrentOrg();

          if (user && org && user.id && user.email) {
            setUser(user);
            setOrg(org);
          } else {
            // If cookies exist but data is invalid, clear them
            authService.logout();
          }
        }
      } catch (error) {
        console.error("Auth check failed:", error);
        authService.logout();
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, []);

  const login = async (credentials: {
    email: string;
    password: string;
  }): Promise<AuthData> => {
    try {
      const data = await authService.signIn(credentials);
      setUser(data.user);
      setOrg(data.org);
      return data;
    } catch (error) {
      throw error;
    }
  };

  const signup = async (userData: {
    email: string;
    password: string;
    name: string;
  }): Promise<AuthData> => {
    try {
      const data = await authService.signUp(userData);
      setUser(data.user);
      setOrg(data.org);
      return data;
    } catch (error) {
      throw error;
    }
  };

  const loginWithGoogle = async (
    googleToken: string,
    orgData?: any
  ): Promise<AuthData> => {
    try {
      const data = await authService.signInWithGoogle(googleToken, orgData);
      setUser(data.user);
      setOrg(data.org);
      return data;
    } catch (error) {
      throw error;
    }
  };

  const getMe = async (): Promise<AuthData> => {
    try {
      const data = await authService.getMe();
      setUser(data.user);
      setOrg(data.org);
      return data;
    } catch (error) {
      throw error;
    }
  };

  const logout = () => {
    authService.logout();
    setUser(null);
    setOrg(null);
  };

  const value: AuthContextType = {
    user,
    org,
    login,
    signup,
    loginWithGoogle,
    getMe,
    logout,
    isAuthenticated: !!user,
    loading,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
